import React, { useState, useRef, useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ThemeProvider, useTheme } from "../context/ThemeContext";
import {
	Home,
	Search,
	Zap,
	Receipt,
	Gauge,
	MessageSquare,
	Phone,
	ArrowRight,
	AlertTriangle,
	RefreshCw,
	Sparkles,
	ChevronRight,
	Sliders,
	ShieldCheck,
	Activity,
	MapPin,
	LifeBuoy,
} from "lucide-react";

/* ─── High-Tech Ambient Grid Canvas ─── */
const GridCanvas: React.FC<{ isDark: boolean; energized: boolean }> = ({ isDark, energized }) => {
	const canvasRef = useRef<HTMLCanvasElement>(null);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		let animationFrameId: number;
		let width = (canvas.width = canvas.offsetWidth);
		let height = (canvas.height = canvas.offsetHeight);

		const handleResize = () => {
			if (!canvas) return;
			width = canvas.width = canvas.offsetWidth;
			height = canvas.height = canvas.offsetHeight;
		};
		window.addEventListener("resize", handleResize);

		// Grid nodes and electricity pulses
		const nodes: Array<{ x: number; y: number; vx: number; vy: number; radius: number; pulse: number }> = [];
		const nodeCount = Math.min(35, Math.floor((width * height) / 28000));

		for (let i = 0; i < nodeCount; i++) {
			nodes.push({
				x: Math.random() * width,
				y: Math.random() * height,
				vx: (Math.random() - 0.5) * 0.4,
				vy: (Math.random() - 0.5) * 0.4,
				radius: Math.random() * 2 + 1,
				pulse: Math.random() * Math.PI * 2,
			});
		}

		let frame = 0;

		const render = () => {
			ctx.clearRect(0, 0, width, height);
			frame += 0.02;

			// Draw connecting circuit lines
			for (let i = 0; i < nodes.length; i++) {
				for (let j = i + 1; j < nodes.length; j++) {
					const dx = nodes[i].x - nodes[j].x;
					const dy = nodes[i].y - nodes[j].y;
					const dist = Math.sqrt(dx * dx + dy * dy);

					if (dist < 130) {
						const alpha = (1 - dist / 130) * (energized ? 0.35 : 0.12);
						ctx.beginPath();
						ctx.moveTo(nodes[i].x, nodes[i].y);
						ctx.lineTo(nodes[j].x, nodes[j].y);
						ctx.strokeStyle = energized
							? `rgba(16, 185, 129, ${alpha})`
							: isDark
								? `rgba(244, 63, 94, ${alpha * 0.7})`
								: `rgba(225, 29, 72, ${alpha * 0.5})`;
						ctx.lineWidth = 1;
						ctx.stroke();
					}
				}
			}

			// Draw glowing nodes
			for (let i = 0; i < nodes.length; i++) {
				const n = nodes[i];
				n.x += n.vx;
				n.y += n.vy;

				if (n.x < 0) n.x = width;
				if (n.x > width) n.x = 0;
				if (n.y < 0) n.y = height;
				if (n.y > height) n.y = 0;

				const currentRadius = n.radius + Math.sin(frame + n.pulse) * 0.8;

				ctx.beginPath();
				ctx.arc(n.x, n.y, Math.max(0.5, currentRadius), 0, Math.PI * 2);
				ctx.fillStyle = energized
					? `rgba(52, 211, 153, ${0.4 + Math.sin(frame) * 0.2})`
					: isDark
						? `rgba(251, 113, 133, 0.4)`
						: `rgba(225, 29, 72, 0.35)`;
				ctx.fill();
			}

			animationFrameId = requestAnimationFrame(render);
		};

		render();

		return () => {
			window.removeEventListener("resize", handleResize);
			cancelAnimationFrame(animationFrameId);
		};
	}, [isDark, energized]);

	return (
		<canvas
			ref={canvasRef}
			className="absolute inset-0 w-full h-full pointer-events-none z-0"
		/>
	);
};

/* ─── Searchable Pages Index ─── */
const searchablePages = [
	{ title: "Home Page", url: "/", category: "General" },
	{ title: "Log a Complaint Ticket", url: "/customer/log-complaints", category: "Customer Care" },
	{ title: "Report Power Outage", url: "/customer/report-outage", category: "Outage Desk" },
	{ title: "Payment Offices & Service Centres", url: "/customer/payment-offices", category: "Locations" },
	{ title: "Band Tariff Schedule", url: "/customer/band-tariff", category: "Billing" },
	{ title: "Prepaid Meter Upgrade (TID)", url: "/metering/prepaid-meter-upgrade", category: "Metering" },
	{ title: "MAP Metering Process", url: "/metering/map-process", category: "Metering" },
	{ title: "Approved MAP Prices", url: "/metering/map-prices", category: "Pricing" },
	{ title: "New Customer Connection", url: "/metering/new-customer-connection", category: "Application" },
	{ title: "Fire & Electrical Safety Tips", url: "/customer/fire-safety-tips", category: "Safety" },
	{ title: "Whistleblowing & Ethics Portal", url: "/customer/whistleblowing", category: "Ethics" },
	{ title: "Customer FAQs", url: "/customer/faqs", category: "Support" },
	{ title: "E-Payment Centers", url: "/e-payment-centers", category: "Payment" },
	{ title: "Contact Us & Helpdesk", url: "/customer/contact", category: "Support" },
	{ title: "About Kaduna Electric", url: "/about/our-company", category: "Corporate" },
];

/* ─── 404 Main Content ─── */
const NotFoundContent: React.FC = () => {
	const { isDark } = useTheme();
	const [energized, setEnergized] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [showSearch, setShowSearch] = useState(false);

	const searchResults = searchQuery.trim()
		? searchablePages.filter(
			(p) =>
				p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
				p.category.toLowerCase().includes(searchQuery.toLowerCase())
		)
		: [];

	const handleToggleBreaker = () => {
		setEnergized(!energized);
	};

	return (
		<div
			className={`min-h-screen transition-colors duration-300 flex flex-col ${isDark ? "bg-[#090C10] text-white" : "bg-[#F8FAFC] text-slate-900"
				}`}
		>
			<Navbar />

			{/* ─── Main Interactive 404 Stage ─── */}
			<main className="relative flex-1 flex flex-col justify-center overflow-hidden">
				<GridCanvas isDark={isDark} energized={energized} />

				{/* Radial Ambient Glow */}
				<div
					className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] pointer-events-none blur-[140px] opacity-35"
					style={{
						background: energized
							? "radial-gradient(circle, #10B981 0%, rgba(5, 150, 105, 0.2) 60%, transparent 80%)"
							: isDark
								? "radial-gradient(circle, #F43F5E 0%, rgba(16, 185, 129, 0.15) 50%, transparent 80%)"
								: "radial-gradient(circle, #FDA4AF 0%, rgba(52, 211, 153, 0.2) 50%, transparent 80%)",
					}}
				/>

				<div className="max-w-[1240px] mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 relative z-10 w-full">
					{/* Breadcrumbs */}
					<div
						style={{ marginBottom: "16px" }}
						className={`flex items-center justify-center gap-2 text-[0.72rem] font-semibold uppercase tracking-wider ${isDark ? "text-emerald-400/80" : "text-emerald-700"
							}`}
					>
						<a href="/" className="hover:underline transition-colors no-underline text-inherit">Home</a>
						<ChevronRight size={12} className="opacity-50" />
						<span className={isDark ? "text-slate-400" : "text-slate-600 font-bold"}>Error 404</span>
					</div>

					{/* Centerstage Hero Card */}
					<div className="text-center max-w-3xl mx-auto">
						{/* Status Badge */}
						<div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold backdrop-blur-md mb-5 shadow-sm border transition-all duration-300"
							style={{
								backgroundColor: energized
									? (isDark ? "rgba(16, 185, 129, 0.12)" : "#ECFDF5")
									: (isDark ? "rgba(244, 63, 94, 0.12)" : "#FFF1F2"),
								borderColor: energized
									? (isDark ? "rgba(16, 185, 129, 0.3)" : "rgba(16, 185, 129, 0.3)")
									: (isDark ? "rgba(244, 63, 94, 0.3)" : "rgba(244, 63, 94, 0.25)"),
								color: energized ? "#10B981" : isDark ? "#FDA4AF" : "#E11D48",
							}}
						>
							<span className="relative flex h-2 w-2">
								<span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${energized ? "bg-emerald-400" : "bg-rose-400"}`} />
								<span className={`relative inline-flex rounded-full h-2 w-2 ${energized ? "bg-emerald-500" : "bg-rose-500"}`} />
							</span>
							<span className="tracking-wide">
								{energized ? "Feeder Energized • Line Synchronized (33kV)" : "Circuit Tripped • Feeder Disconnected (0 kV)"}
							</span>
						</div>

						{/* ─── Ultra Graphic 404 Display ─── */}
						<div className="relative my-2 select-none">
							<div className="flex items-center justify-center gap-2 sm:gap-4">
								<span
									className="text-7xl sm:text-9xl lg:text-[11rem] font-black tracking-tighter leading-none"
									style={{
										background: energized
											? "linear-gradient(180deg, #34D399 0%, #059669 100%)"
											: isDark
												? "linear-gradient(180deg, #FFFFFF 0%, #475569 100%)"
												: "linear-gradient(180deg, #0F172A 0%, #94A3B8 100%)",
										WebkitBackgroundClip: "text",
										WebkitTextFillColor: "transparent",
										textShadow: energized ? "0 0 60px rgba(16, 185, 129, 0.4)" : "none",
									}}
								>
									4
								</span>

								{/* Center Animated Substation Indicator in place of 0 */}
								<div className="relative flex items-center justify-center">
									<div
										className="w-20 h-20 sm:w-28 sm:h-28 lg:w-36 lg:h-36 rounded-full flex items-center justify-center border-2 transition-all duration-500 relative"
										style={{
											borderColor: energized ? "#10B981" : isDark ? "rgba(244, 63, 94, 0.4)" : "rgba(225, 29, 72, 0.3)",
											background: energized
												? "radial-gradient(circle, rgba(16, 185, 129, 0.2) 0%, transparent 70%)"
												: isDark
													? "radial-gradient(circle, rgba(244, 63, 94, 0.15) 0%, transparent 70%)"
													: "radial-gradient(circle, rgba(244, 63, 94, 0.08) 0%, transparent 70%)",
											boxShadow: energized ? "0 0 40px rgba(16, 185, 129, 0.35)" : "none",
										}}
									>
										{/* Inner pulsing ring */}
										<div
											className={`absolute inset-2 rounded-full border border-dashed transition-all duration-700 ${energized ? "border-emerald-400/60 animate-spin" : "border-rose-400/30"
												}`}
											style={{ animationDuration: "12s" }}
										/>

										<button
											type="button"
											onClick={handleToggleBreaker}
											title="Click to toggle circuit breaker test"
											className="relative z-10 w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full flex flex-col items-center justify-center cursor-pointer transition-transform hover:scale-105 active:scale-95 shadow-md"
											style={{
												background: energized
													? "linear-gradient(180deg, #10B981 0%, #059669 100%)"
													: isDark
														? "#1E293B"
														: "#FFFFFF",
												color: energized ? "#FFFFFF" : isDark ? "#F43F5E" : "#E11D48",
												borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)",
											}}
										>
											{energized ? <Zap size={24} className="animate-pulse" /> : <AlertTriangle size={24} />}
											<span className="text-[0.55rem] sm:text-[0.62rem] font-extrabold uppercase mt-0.5 tracking-tight">
												{energized ? "ON" : "RESET"}
											</span>
										</button>
									</div>
								</div>

								<span
									className="text-7xl sm:text-9xl lg:text-[11rem] font-black tracking-tighter leading-none"
									style={{
										background: energized
											? "linear-gradient(180deg, #34D399 0%, #059669 100%)"
											: isDark
												? "linear-gradient(180deg, #FFFFFF 0%, #475569 100%)"
												: "linear-gradient(180deg, #0F172A 0%, #94A3B8 100%)",
										WebkitBackgroundClip: "text",
										WebkitTextFillColor: "transparent",
										textShadow: energized ? "0 0 60px rgba(16, 185, 129, 0.4)" : "none",
									}}
								>
									4
								</span>
							</div>
						</div>

						{/* Headline & Description */}
						<h2
							style={{ marginBottom: "10px" }}
							className={`text-xl sm:text-2xl lg:text-3xl font-extrabold tracking-tight ${isDark ? "text-white" : "text-slate-900"
								}`}
						>
							{energized ? "Feeder Online! Re-routing you home..." : "We Couldn't Find That Power Link"}
						</h2>

						<p
							style={{ marginBottom: "22px" }}
							className={`text-xs sm:text-sm leading-relaxed max-w-lg mx-auto ${isDark ? "text-slate-400" : "text-slate-600"
								}`}
						>
							{energized
								? "All transmission parameters nominal. Click below to return to the primary Kaduna Electric portal."
								: "The page you are trying to reach has either been relocated, de-energized, or never connected to the regional distribution network."}
						</p>

						{/* Quick Action Navigation Buttons */}
						<div className="flex flex-wrap items-center justify-center gap-3" style={{ marginBottom: "28px" }}>
							<a
								href="/"
								style={{
									background: "linear-gradient(180deg, #10B981 0%, #059669 100%)",
								}}
								className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full text-xs sm:text-sm font-semibold text-white shadow-md shadow-emerald-900/25 hover:opacity-95 hover:-translate-y-0.5 active:scale-95 transition-all duration-300 no-underline"
							>
								<Home size={15} />
								<span>Return to Main Grid (Home)</span>
							</a>

							<button
								type="button"
								onClick={() => setShowSearch(!showSearch)}
								style={{
									background: isDark ? "rgba(255, 255, 255, 0.06)" : "#FFFFFF",
									borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)",
								}}
								className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold border backdrop-blur-md hover:-translate-y-0.5 active:scale-95 transition-all duration-300 cursor-pointer shadow-2xs ${isDark ? "text-white hover:bg-white/10" : "text-slate-800 hover:bg-slate-50"
									}`}
							>
								<Search size={14} className="text-emerald-500" />
								<span>Search Website</span>
							</button>

							<a
								href="/customer/report-outage"
								style={{
									background: isDark ? "rgba(255, 255, 255, 0.06)" : "#FFFFFF",
									borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)",
								}}
								className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold border backdrop-blur-md hover:-translate-y-0.5 active:scale-95 transition-all duration-300 no-underline shadow-2xs ${isDark ? "text-white hover:bg-white/10" : "text-slate-800 hover:bg-slate-50"
									}`}
							>
								<Zap size={14} className="text-amber-500" />
								<span>Report Fault</span>
							</a>
						</div>

						{/* Expandable Instant Search Bar */}
						{showSearch && (
							<div
								style={{
									background: isDark
										? "linear-gradient(180deg, rgba(255, 255, 255, 0.04) 0%, rgba(194, 196, 200, 0.02) 100%)"
										: "#FFFFFF",
									borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)",
									padding: "16px",
									marginBottom: "28px",
								}}
								className="rounded-2xl border max-w-xl mx-auto text-left shadow-lg"
							>
								<div className="relative">
									<Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-emerald-500" />
									<input
										type="text"
										autoFocus
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
										placeholder="Type to search (e.g. Band Tariff, Meter Upgrade, Payment, Safety)..."
										style={{
											padding: "10px 14px 10px 38px",
											borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)",
										}}
										className={`w-full rounded-xl border text-xs sm:text-sm outline-none transition-all ${isDark
												? "bg-black/30 text-white placeholder-slate-500 focus:border-emerald-500"
												: "bg-[#F8FAFC] text-slate-900 placeholder-slate-400 focus:bg-white focus:border-emerald-600"
											}`}
									/>
								</div>

								{searchQuery.trim().length > 0 && (
									<div className="mt-2.5 max-h-48 overflow-y-auto flex flex-col gap-1 pt-1 border-t border-slate-200/60 dark:border-white/10">
										{searchResults.length === 0 ? (
											<p className="text-xs text-slate-400 py-3 text-center">
												No matching pages found for "{searchQuery}"
											</p>
										) : (
											searchResults.map((item, idx) => (
												<a
													key={idx}
													href={item.url}
													className="flex items-center justify-between p-2 rounded-lg hover:bg-emerald-500/10 transition-colors no-underline text-inherit group"
												>
													<div className="flex items-center gap-2">
														<Sparkles size={12} className="text-emerald-500" />
														<span className="text-xs font-semibold group-hover:text-emerald-500">{item.title}</span>
													</div>
													<span className="text-[0.66rem] font-bold text-slate-400 group-hover:text-emerald-500 flex items-center gap-1">
														{item.category} <ArrowRight size={10} />
													</span>
												</a>
											))
										)}
									</div>
								)}
							</div>
						)}
					</div>

					{/* ─── 4 Bento Quick-Connect Cards ─── */}
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-4 max-w-5xl mx-auto">
						{[
							{
								icon: <Receipt size={18} className="text-emerald-500" />,
								title: "Tariff & Payments",
								desc: "MYTO Band Tariff schedules, vendor centers, and account payment channels.",
								href: "/customer/band-tariff",
								linkText: "View Tariff Band",
							},
							{
								icon: <Gauge size={18} className="text-sky-500" />,
								title: "Prepaid Meter Hub",
								desc: "MAP meter applications, pricing index, and TID Rollover upgrades.",
								href: "/metering/prepaid-meter-upgrade",
								linkText: "Prepaid Upgrades",
							},
							{
								icon: <MessageSquare size={18} className="text-teal-500" />,
								title: "Log a Complaint",
								desc: "Statutory NERC 24-hour resolution dispatch and complaint ticket tracking.",
								href: "/customer/log-complaints",
								linkText: "Open Ticket Desk",
							},
							{
								icon: <Phone size={18} className="text-amber-500" />,
								title: "24/7 Call Centre",
								desc: "Speak with a live dispatch engineer via our toll-free support line.",
								href: "tel:08005238623",
								linkText: "0800 5238 623",
							},
						].map((card, i) => (
							<a
								key={i}
								href={card.href}
								style={{
									padding: "18px 20px",
									background: isDark
										? "linear-gradient(180deg, rgba(255, 255, 255, 0.04) 0%, rgba(194, 196, 200, 0.02) 100%)"
										: "#FFFFFF",
									borderColor: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)",
								}}
								className="rounded-2xl border transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/40 no-underline text-inherit group shadow-2xs flex flex-col justify-between"
							>
								<div>
									<div className="flex items-center gap-2.5 mb-2">
										<div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${isDark ? "bg-white/[0.06]" : "bg-slate-100"}`}>
											{card.icon}
										</div>
										<h3 className="text-xs sm:text-sm font-bold leading-tight group-hover:text-emerald-500 transition-colors">
											{card.title}
										</h3>
									</div>
									<p className={`text-[0.72rem] leading-relaxed mb-3 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
										{card.desc}
									</p>
								</div>

								<div
									style={{
										borderColor: isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.06)",
									}}
									className="pt-2 border-t flex items-center justify-between text-xs font-semibold text-emerald-500"
								>
									<span>{card.linkText}</span>
									<ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
								</div>
							</a>
						))}
					</div>
				</div>
			</main>

			<Footer />
		</div>
	);
};

/* ─── Main Export Wrapped with ThemeProvider ─── */
const NotFoundPage: React.FC = () => {
	return (
		<ThemeProvider>
			<NotFoundContent />
		</ThemeProvider>
	);
};

export default NotFoundPage;
